import { formatAmount } from "@/lib/utils";
import { RootState } from "@/redux/store";
import { useGetARDetailedReport } from "@/services";
import moment from "moment";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

const useDetailedReport = ({ reportDate }: { reportDate: any }) => {
  const [summaryCard, setSummaryCard] = useState([
    {
      title: "# Customers",
      count: 0,
      key: "totalCustomer",
    },
    {
      title: "Outstanding A/R",
      count: 0,
      key: "totalOutstanding",
    },
    {
      title: "Uncleared A/R",
      count: 0,
      key: "unclearedAr",
    },
    {
      title: "Uncleared Collections",
      count: 0,
      key: "openCollection",
    },
  ]);
  const communityDetail = useSelector(
    (state: RootState) => state.currentData.communityData
  );

  const {
    data: detailedReport,
    isLoading: isLoadingDetailedReport,
    refetch: refetchDetailedReport,
  } = useGetARDetailedReport({
    communityId: communityDetail?.communityId,
    ...(reportDate
      ? { reportDate: moment(reportDate).format("YYYY-MM-DD") }
      : {}),
    queryParams: {
      enabled: !!communityDetail?.communityId,
    },
  });

  useEffect(() => {
    refetchDetailedReport();
  }, [reportDate]);

  useEffect(() => {
    if (detailedReport?.data) {
      let unclearedAr = 0,
        openCollection = 0,
        outstanding = 0; // outstanding is total of pendingBillingAmount, collectionsAmount and otherChargesAmount;

      const detailedData = detailedReport?.data;

      detailedData?.summary?.map((data: any) => {
        if (data?.category?.toLowerCase() == "total") {
          unclearedAr = data.pendingBillingAmount;
          openCollection = -data.collectionsAmount;
          outstanding =
            data.pendingBillingAmount +
            data.otherChargesAmount -
            data.collectionsAmount;
        }
      });

      const cards = summaryCard?.map((data: any) => {
        if (data.key == "unclearedAr") {
          return {
            ...data,
            count: unclearedAr,
          };
        }

        if (data.key == "totalOutstanding") {
          return {
            ...data,
            count: outstanding,
          };
        }

        if (data.key == "openCollection") {
          return {
            ...data,
            count: openCollection,
          };
        }

        return {
          ...data,
          count: data?.key?.toLowerCase()?.includes("amount")
            ? formatAmount(detailedData?.[data?.key] || "0")
            : detailedData?.[data?.key] || "0",
        };
      });

      setSummaryCard([...cards]);
    }
  }, [detailedReport]);
  // Define Table Columns
  const columns = [
    {
      accessorKey: "category",
      header: <div></div>,
    },
    // {
    //   accessorKey: "totalBillingAmount",
    //   header: <div>Total Billing</div>,
    //   cell: (row: any) => {
    //     return row?.row.original?.category?.toLowerCase()?.includes("count")
    //       ? row?.row.original?.totalBillingAmount
    //       : formatAmount(row?.row.original?.totalBillingAmount || "0");
    //   },
    // },
    {
      accessorKey: "outstandingAr",
      meta: { align: "right" },
      header: <div>Outstanding A/R</div>,
      cell: (row: any) => {
        return row?.row.original?.category?.toLowerCase()?.includes("count")
          ? row?.row.original?.pendingBillingAmount +
              row?.row.original?.otherChargesAmount -
              row?.row.original?.collectionsAmount
          : formatAmount(
              row?.row.original?.pendingBillingAmount +
                row?.row.original?.otherChargesAmount -
                row?.row.original?.collectionsAmount || "0"
            );
      },
    },
    {
      accessorKey: "pendingBillingAmount",
      meta: { align: "right" },
      header: <div>Uncleared A/R</div>,
      cell: (row: any) => {
        return row?.row.original?.category?.toLowerCase()?.includes("count")
          ? row?.row.original?.pendingBillingAmount
          : formatAmount(row?.row.original?.pendingBillingAmount || "0");
      },
    },
    {
      accessorKey: "otherChargesAmount",
      meta: { align: "right" },
      header: <div>Uncleared Other Chg.</div>,
      cell: (row: any) => {
        return row?.row.original?.category?.toLowerCase()?.includes("count")
          ? row?.row.original?.otherChargesAmount
          : formatAmount(row?.row.original?.otherChargesAmount || "0");
      },
    },
    {
      accessorKey: "collectionsAmount",
      meta: { align: "right" },
      header: <div>Uncleared Collections</div>,
      cell: (row: any) => {
        return row?.row.original?.category?.toLowerCase()?.includes("count")
          ? row?.row.original?.collectionsAmount
          : formatAmount(
              Number(row?.row.original?.collectionsAmount) === 0
                ? "0"
                : `-${row?.row.original?.collectionsAmount}`
            );
      },
    },
    {
      accessorKey: "agingBucketNotDue",
      meta: { align: "right" },
      header: <div>Not Due</div>,
      cell: (row: any) => {
        return row?.row.original?.category?.toLowerCase()?.includes("count")
          ? row?.row.original?.agingBucketNotDue
          : formatAmount(row?.row.original?.agingBucketNotDue || "0");
      },
    },
    {
      accessorKey: "agingBucket0To30",
      meta: { align: "right" },
      header: (
        <div>
          Due In <br /> 0-30 Days
        </div>
      ),
      cell: (row: any) => {
        return row?.row.original?.category?.toLowerCase()?.includes("count")
          ? row?.row.original?.agingBucket0To30
          : formatAmount(row?.row.original?.agingBucket0To30 || "0");
      },
    },
    {
      accessorKey: "agingBucket31To60",
      meta: { align: "right" },
      header: (
        <div>
          Due In <br />
          30-60 Days
        </div>
      ),
      cell: (row: any) => {
        return row?.row.original?.category?.toLowerCase()?.includes("count")
          ? row?.row.original?.agingBucket31To60
          : formatAmount(row?.row.original?.agingBucket31To60 || "0");
      },
    },
    {
      accessorKey: "agingBucket61To90",
      meta: { align: "right" },
      header: (
        <div>
          Due In <br />
          60-90 Days
        </div>
      ),
      cell: (row: any) => {
        return row?.row.original?.category?.toLowerCase()?.includes("count")
          ? row?.row.original?.agingBucket61To90
          : formatAmount(row?.row.original?.agingBucket61To90 || "0");
      },
    },
    {
      accessorKey: "agingBucketOver90",
      meta: { align: "right" },
      header: (
        <div>
          Due <br />
          Over 90 Days
        </div>
      ),
      cell: (row: any) => {
        return row?.row.original?.category?.toLowerCase()?.includes("count")
          ? row?.row.original?.agingBucketOver90
          : formatAmount(row?.row.original?.agingBucketOver90 || "0");
      },
    },
  ];

  return {
    summaryCard,
    columns,
    isLoadingDetailedReport,
    detailedReport,
  };
};

export default useDetailedReport;
